import S, { maybeShape } from 'common/service/sanctuary';
import { is } from 'ramda';
import { AjaxError, AjaxTimeoutError } from 'rxjs/ajax';
import React from 'react';
import { node, bool, shape } from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import Empty from '../empty';
import Error from '../error';
import { PublicError } from '../../../service/error';
import withErrorBoundary from '../../../components/withErrorBoundary';

const resourceMessages = defineMessages({
  loading: {
    id: 'mdc.wrappers.resource.loading',
    defaultMessage: 'Loading ...',
  },
  notFound: {
    id: 'mdc.wrappers.resource.notFound',
    defaultMessage: '{code} - Not found',
  },
  timeout: {
    id: 'mdc.wrappers.resource.timeout',
    defaultMessage: 'Request timeout',
  },
  cancelled: {
    id: 'mdc.wrappers.resource.cancelled',
    defaultMessage: 'Request has been cancelled',
  },
  ajaxError: {
    id: 'mdc.wrappers.error.serverError',
    defaultMessage: 'Error {statusCode}',
  },
});

export const getAjaxErrorTitle = (error) => {
  if (is(AjaxTimeoutError, error)) {
    return <FormattedMessage {...resourceMessages.timeout} />;
  }

  if (error.status === 0) {
    return <FormattedMessage {...resourceMessages.cancelled} />;
  }

  return (
    <FormattedMessage
      values={{
        statusCode: <b>{error.status}</b>,
      }}
      {...resourceMessages.ajaxError}
    />
  );
};

export const getAjaxErrorBody = ({ response }) => (
  response && (response.message
    ? response.message
    : JSON.stringify(response)
  )
);

const resourceError = (messages = {}) => (error) => {
  if (is(PublicError, error)) {
    return (
      <Error title={error.title}>
        {error.message}
      </Error>
    );
  }

  if (is(AjaxError, error)) {
    if (error.status === 404) {
      return (
        <Empty
          title={messages.empty || (
            <FormattedMessage
              values={{ code: <b>{error.status}</b> }}
              {...resourceMessages.notFound}
            />
          )}
          reasons={messages.emptyReasons}
        />
      );
    }

    return (
      <Error title={getAjaxErrorTitle(error)}>
        {getAjaxErrorBody(error)}
      </Error>
    );
  }

  throw error;
};

const Loading = ({ loading, message }) => loading && (
  <span className="resource-loading">
    {message || <FormattedMessage {...resourceMessages.loading} />}
  </span>
);

const ResourceWrapper = ({
  loading,
  context, // Maybe (Either a)
  messages,
  children,
}) => S.maybe(
  <Loading loading={loading} message={messages.loading} />,
  S.either(
    resourceError(messages),
    resource => (
      is(Function, children)
        ? children(resource)
        : children
    ),
  ),
  context,
);

ResourceWrapper.propTypes = {
  loading: bool,
  context: maybeShape,
  messages: shape({
    loading: node,
    empty: node,
    emptyReasons: node,
  }),
};

ResourceWrapper.defaultProps = {
  loading: false,
  messages: {},
};

export default withErrorBoundary(ResourceWrapper);
